import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { endOfDay, format, parse, subDays } from 'date-fns';
import { DocumentType, TimeSeriesGrouping } from '@/api/generated-types';
import type { AnalyticsFilters } from '@/api/types';

const DATE_FORMAT = 'yyyy-MM-dd';

function parseDocumentType(value: string | null): DocumentType | undefined {
  return value === DocumentType.Dgd || value === DocumentType.Dcm ? value : undefined;
}

function parseDate(value: string | null, fallback: Date): Date {
  if (!value) return fallback;
  const parsed = parse(value, DATE_FORMAT, new Date());
  return isNaN(parsed.getTime()) ? fallback : parsed;
}

function parseGroupBy(value: string | null): TimeSeriesGrouping {
  if (!value) return TimeSeriesGrouping.None;
  const num = Number(value);
  if (num in TimeSeriesGrouping) return num as TimeSeriesGrouping;
  return TimeSeriesGrouping.None;
}

function computeEffectiveGroupBy(
  groupBy: TimeSeriesGrouping,
  from: Date,
  to: Date,
): TimeSeriesGrouping {
  if (groupBy !== TimeSeriesGrouping.None) return groupBy;
  const days = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 14) return TimeSeriesGrouping.Day;
  if (days <= 90) return TimeSeriesGrouping.Week;
  return TimeSeriesGrouping.Month;
}

/**
 * Pure function for use in route loaders (no hooks).
 * Parses URL search params into AnalyticsFilters for API calls.
 */
export function parseAnalyticsFilters(searchParams: URLSearchParams): AnalyticsFilters {
  const now = new Date();
  const from = parseDate(searchParams.get('from'), subDays(now, 30));
  const to = parseDate(searchParams.get('to'), now);
  const teamId = searchParams.get('teamId') || undefined;
  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const effectiveGroupBy = computeEffectiveGroupBy(groupBy, from, to);

  return {
    from: from.toISOString(),
    to: endOfDay(to).toISOString(),
    teamId,
    groupBy: effectiveGroupBy,
    documentType: parseDocumentType(searchParams.get('docType')),
  };
}

/**
 * Hook for reading/writing analytics filter state from URL search params.
 * Source of truth for date range, team, and groupBy across the analytics layout and tabs.
 */
export function useAnalyticsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dateRange = useMemo(() => {
    const now = new Date();
    return {
      from: parseDate(searchParams.get('from'), subDays(now, 30)),
      to: parseDate(searchParams.get('to'), now),
    };
  }, [searchParams]);

  const selectedTeamId = searchParams.get('teamId') || null;
  const documentType = parseDocumentType(searchParams.get('docType'));
  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const effectiveGroupBy = useMemo(
    () => computeEffectiveGroupBy(groupBy, dateRange.from, dateRange.to),
    [groupBy, dateRange],
  );

  const filters: AnalyticsFilters = useMemo(() => ({
    from: dateRange.from.toISOString(),
    to: endOfDay(dateRange.to).toISOString(),
    teamId: selectedTeamId ?? undefined,
    groupBy: effectiveGroupBy,
    documentType,
  }), [dateRange, selectedTeamId, effectiveGroupBy, documentType]);

  const previousFilters: AnalyticsFilters = useMemo(() => {
    const spanMs = dateRange.to.getTime() - dateRange.from.getTime();
    const prevFrom = new Date(dateRange.from.getTime() - spanMs);
    return {
      from: prevFrom.toISOString(),
      to: dateRange.from.toISOString(),
      teamId: selectedTeamId ?? undefined,
      groupBy: TimeSeriesGrouping.None,
      documentType,
    };
  }, [dateRange, selectedTeamId, documentType]);

  const deltaLabel = useMemo(() => {
    const days = Math.round((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));
    return `prev ${days}d`;
  }, [dateRange]);

  const setDateRange = useCallback((range: { from?: Date; to?: Date }) => {
    setSearchParams((prev) => {
      if (range.from) prev.set('from', format(range.from, DATE_FORMAT));
      else prev.delete('from');
      if (range.to) prev.set('to', format(range.to, DATE_FORMAT));
      else prev.delete('to');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const setSelectedTeamId = useCallback((teamId: string | null) => {
    setSearchParams((prev) => {
      if (teamId) prev.set('teamId', teamId);
      else prev.delete('teamId');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const setDocumentType = useCallback((value: DocumentType | null) => {
    setSearchParams((prev) => {
      if (value) prev.set('docType', value);
      else prev.delete('docType');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const setGroupBy = useCallback((value: TimeSeriesGrouping) => {
    setSearchParams((prev) => {
      if (value !== TimeSeriesGrouping.None) prev.set('groupBy', String(value));
      else prev.delete('groupBy');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const refreshToNow = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('to', format(new Date(), DATE_FORMAT));
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    dateRange,
    filters,
    previousFilters,
    deltaLabel,
    groupBy,
    effectiveGroupBy,
    selectedTeamId,
    documentType,
    setDateRange,
    setSelectedTeamId,
    setDocumentType,
    setGroupBy,
    refreshToNow,
  };
}
